import { useEffect, useRef, useState } from 'react';
import { useExpandableAccordionContext } from '../components/Monetize/ExpandableAccordion';

interface UseAccordionControlOptions {
  isAllExpanded?: boolean;
  expandCommandSeq?: number;
  onExpandChange?: (isExpanded: boolean) => void;
  itemCount?: number;
  defaultIsExpanded?: boolean;
  /**
   * Values open on mount, for opening only some items. Takes precedence over
   * `defaultIsExpanded`, and like it is reported to `onExpandChange` so an
   * enclosing expand-all button starts in sync.
   */
  defaultValue?: string[];
}

// v3's Accordion.Root tracks expanded items by string `value`, not numeric
// index - each consumer's items are rendered positionally (`.map((item,
// index) => <Accordion.Item value={String(index)}>`), so this hook
// represents the same "which positions are open" state as stringified
// indices rather than numbers.
export function useAccordionControl({
  isAllExpanded,
  expandCommandSeq,
  onExpandChange,
  itemCount = 1,
  defaultIsExpanded = false,
  defaultValue,
}: UseAccordionControlOptions = {}) {
  const ctx = useExpandableAccordionContext();

  const effectiveIsAllExpanded = isAllExpanded ?? ctx.isAllExpanded;
  const effectiveExpandCommandSeq = expandCommandSeq ?? ctx.expandCommandSeq;
  const effectiveOnExpandChange = onExpandChange ?? ctx.onExpandChange;

  const [accordionValue, setAccordionValue] = useState<string[]>(
    () =>
      defaultValue ??
      (defaultIsExpanded
        ? Array.from({ length: itemCount }, (_, index) => String(index))
        : []),
  );
  const isAllExpandedRef = useRef(effectiveIsAllExpanded);
  isAllExpandedRef.current = effectiveIsAllExpanded;
  const onExpandChangeRef = useRef(effectiveOnExpandChange);
  onExpandChangeRef.current = effectiveOnExpandChange;

  useEffect(() => {
    if (accordionValue.length > 0) {
      onExpandChangeRef.current?.(true);
    }
  }, []);

  useEffect(() => {
    if ((effectiveExpandCommandSeq ?? 0) > 0) {
      const shouldExpand = isAllExpandedRef.current;
      const newValues = shouldExpand
        ? Array.from({ length: itemCount }, (_, i) => String(i))
        : [];
      setAccordionValue(newValues);
      onExpandChangeRef.current?.(shouldExpand);
    }
  }, [effectiveExpandCommandSeq, itemCount]);

  const handleAccordionChange = (values: string[]) => {
    const wasOpen = accordionValue.length > 0;
    const isNowOpen = values.length > 0;
    setAccordionValue(values);
    if (wasOpen !== isNowOpen) {
      effectiveOnExpandChange?.(isNowOpen);
    }
  };

  return { accordionValue, handleAccordionChange, setAccordionValue };
}
